import { authHeaders, fetchWithAuthRetry, getApiUrl, parseApiError } from './common';
import type { PagedData } from './common';

// 1-depth 답글. 플랜/문서 원댓글 모두에 동일한 엔드포인트로 동작한다(targetType 으로 부모 대상을 구분).
export interface CommentReply {
  id: string;
  parentId: string;
  targetType: string;
  targetId: string;
  createdByMemberId: string | null;
  createdByAgentId: string | null;
  createdByType: string | null;
  /** 경유 축. 도구 축을 특정하지 못해도 웹 작성물과 에이전트 표면 작성물을 구분한다. */
  createdByCredentialKind: string | null;
  createdByName: string | null;
  /** 작성에 사용된 도구(에이전트 설정) 이름. 행위 주체 축과 직교한다. */
  createdByAgentName: string | null;
  updatedByMemberId?: string | null;
  updatedByAgentId?: string | null;
  updatedByCredentialKind?: string | null;
  updatedByName?: string | null;
  updatedByAgentName?: string | null;
  content: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

type ReplyResponse = {
  data: CommentReply;
};

export const fetchCommentReplies = async (
  token: string,
  projectId: string,
  commentId: string,
  params?: { order?: 'asc' | 'desc' },
): Promise<PagedData<CommentReply>> => {
  const queryParams = new URLSearchParams();
  if (params?.order) queryParams.set('order', params.order);

  const url = `${getApiUrl()}/api/projects/${projectId}/comments/${commentId}/replies${
    queryParams.toString() ? `?${queryParams.toString()}` : ''
  }`;
  const response = await fetchWithAuthRetry(url, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch replies: ${response.status}`);
  }

  return (await response.json()) as PagedData<CommentReply>;
};

export const createCommentReply = async (
  token: string,
  projectId: string,
  commentId: string,
  input: { content: string },
): Promise<CommentReply> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/comments/${commentId}/replies`, {
    method: 'POST',
    headers: authHeaders(token, true),
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to create reply: ${response.status}`);
  }

  const data = (await response.json()) as ReplyResponse;
  return data.data;
};

export const updateCommentReply = async (
  token: string,
  projectId: string,
  replyId: string,
  input: { content: string },
): Promise<CommentReply> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/comment-replies/${replyId}`, {
    method: 'PUT',
    headers: authHeaders(token, true),
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to update reply: ${response.status}`);
  }

  const data = (await response.json()) as ReplyResponse;
  return data.data;
};

export const deleteCommentReply = async (token: string, projectId: string, replyId: string): Promise<void> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/comment-replies/${replyId}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to delete reply: ${response.status}`);
  }
};
